import { createContainer, createStore } from 'react-sweet-state';
import type { ProjectType } from '@atlassian/jira-common-constants';
import type { Project } from '@atlassian/jira-common-constants/src/project-types';
import { DEFAULT } from '../../common/constants/import-type';
import { CROSS_FLOW, EXISTING_PROJECT } from '../../common/constants/source';
import { INITIAL_STATE } from '../../common/constants/state';
import type { State } from '../../common/types';
import { getFirstStep } from '../../common/utils/import-steps';
import type { ImporterContext } from '../../common/utils/importer-context';
import { actions, type Actions } from './actions';

export const Store = createStore<State, Actions>({
	name: 'ProjectCreateImporterStore',
	initialState: INITIAL_STATE,
	actions,
});

export type Props = {
	projectType: ProjectType | null;
	importerContext: ImporterContext | null;
	project?: Project | null;
};

export const getStateFromProps = ({ projectType, importerContext, project }: Props): State => {
	const type = projectType || INITIAL_STATE.projectData.type;

	if (importerContext === null) {
		if (project) {
			return {
				...INITIAL_STATE,
				step: getFirstStep({
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					projectType: project.type as ProjectType,
					importType: INITIAL_STATE.importType,
					source: EXISTING_PROJECT,
				}),
				projectData: {
					...INITIAL_STATE.projectData,
					key: project.key,
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					type: project.type as ProjectType,
					name: project.name,
				},
				source: EXISTING_PROJECT,
			};
		}

		return {
			...INITIAL_STATE,
			step: getFirstStep({ projectType, importType: INITIAL_STATE.importType }),
			projectData: {
				...INITIAL_STATE.projectData,
				type,
			},
		};
	}

	const { importType } = importerContext;

	if (importType === DEFAULT) {
		return INITIAL_STATE;
	}

	return {
		...INITIAL_STATE,
		step: getFirstStep({
			projectType,
			importType,
			source: CROSS_FLOW,
		}),
		importType,
		thirdPartyState: {
			...INITIAL_STATE.thirdPartyState,
			selectedEntityId: importerContext.entityId,
			selectedWorkspaceId: importerContext.workspaceId,
		},
		projectData: {
			...INITIAL_STATE.projectData,
			type,
		},
		source: CROSS_FLOW,
	};
};

export const Container = createContainer<State, Actions, Props>(Store, {
	onInit:
		() =>
		({ setState }, props) => {
			setState(getStateFromProps(props));
		},
});
